import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

const RecordingNavigationContext = createContext({ setRecordingBusy: () => {}, confirmNavigation: () => true });
export const useRecordingNavigationGuard = () => useContext(RecordingNavigationContext);

// BrowserRouter does not expose the data-router blocker API. Intercept its
// navigator without changing route definitions or recording/billing handlers.
export function RecordingNavigationGuardProvider({ children }) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const busy = useRef(false);
  const index = useRef(window.history.state?.idx);
  const restoring = useRef(false);
  const [open, setOpen] = useState(false);
  const setRecordingBusy = useCallback(value => { busy.current = value; }, []);
  const confirmNavigation = useCallback(() => {
    if (!busy.current) return true;
    setOpen(true);
    return false;
  }, []);

  useLayoutEffect(() => { index.current = window.history.state?.idx; }, [location]);
  useLayoutEffect(() => {
    const originals = {};
    ['push', 'replace', 'go'].forEach(method => {
      originals[method] = navigator[method];
      navigator[method] = (...args) => {
        if (!confirmNavigation()) return;
        const result = originals[method].apply(navigator, args);
        if (method !== 'go') index.current = window.history.state?.idx;
        return result;
      };
    });
    const onPopState = event => {
      const next = event.state?.idx;
      if (restoring.current) {
        event.stopImmediatePropagation();
        if (next === index.current) restoring.current = false;
        else if (Number.isInteger(next)) window.history.go(index.current - next);
        return;
      }
      if (busy.current && Number.isInteger(index.current) && Number.isInteger(next) && next !== index.current) {
        event.stopImmediatePropagation();
        restoring.current = true;
        confirmNavigation();
        window.history.go(index.current - next);
      } else if (!busy.current) index.current = next;
      // Entries outside BrowserRouter have no known index. Cross-document
      // navigation is handled by beforeunload, subject to browser restrictions.
    };
    window.addEventListener('popstate', onPopState, true);
    return () => {
      Object.keys(originals).forEach(method => { navigator[method] = originals[method]; });
      window.removeEventListener('popstate', onPopState, true);
    };
  }, [navigator, confirmNavigation]);

  const value = useMemo(() => ({ setRecordingBusy, confirmNavigation }), [setRecordingBusy, confirmNavigation]);
  return <RecordingNavigationContext.Provider value={value}>
    {children}
    <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby="recording-navigation-title">
      <DialogTitle id="recording-navigation-title">Transcription in progress</DialogTitle>
      <DialogContent><DialogContentText>Please stop any recording and finish saving your transcription before leaving this page.</DialogContentText></DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>Stay on this page</Button></DialogActions>
    </Dialog>
  </RecordingNavigationContext.Provider>;
}
